import React, { useState, useMemo } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { skuForecasts, promoCalendar, skuOrder } from './data/forecastData';

const STATUS_STYLES = {
  CRITICAL: 'bg-red-100 text-red-700',
  ORDER_NOW: 'bg-orange-100 text-orange-700',
  WATCH: 'bg-yellow-100 text-yellow-700',
  HEALTHY: 'bg-green-100 text-green-700',
  OVERSTOCK: 'bg-purple-100 text-purple-700',
};

const STATUS_LABEL = {
  CRITICAL: 'Critical',
  ORDER_NOW: 'Order Now',
  WATCH: 'Watch',
  HEALTHY: 'Healthy',
  OVERSTOCK: 'Overstock',
};

const formatCurrency = (value) => `$${Number(value).toLocaleString('en-US')}`;
const formatUnits = (value) => Number(value).toLocaleString('en-US');

const StatCard = ({ label, value, sub, tone }) => (
  <div className="bg-white border border-white/20 rounded-xl p-4">
    <p className="text-xs text-gray-400">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    {sub && <p className={`text-xs mt-1 ${tone === 'up' ? 'text-emerald-600' : tone === 'down' ? 'text-red-600' : 'text-gray-400'}`}>{sub}</p>}
  </div>
);

const buildInsight = (sku) => {
  if (sku.status === 'CRITICAL') {
    return `${sku.name} is projected to stock out in just ${sku.daysCover} days at current sell-through. Reorder ${formatUnits(sku.reorderQty)} units now to cover the seasonal demand spike forecast for next month.`;
  }
  if (sku.status === 'ORDER_NOW') {
    return `${sku.name} has ${sku.daysCover} days of cover left — below the 20-day reorder threshold. Recommend placing a ${formatUnits(sku.reorderQty)}-unit purchase order this week.`;
  }
  if (sku.status === 'OVERSTOCK') {
    return `${sku.name} is carrying ${sku.daysCover} days of cover, well above target. Consider a promo push or slowing the next replenishment order to work down excess inventory.`;
  }
  if (sku.status === 'WATCH') {
    return `${sku.name} is trending toward its reorder point with ${sku.daysCover} days of cover remaining — worth monitoring over the next forecast cycle.`;
  }
  return `${sku.name} is tracking a healthy ${sku.daysCover} days of cover, with demand up ${sku.yoyGrowth}% year-over-year and a forecast accuracy (MAPE) of ${sku.mape}%.`;
};

const Forecasting = () => {
  const [selectedSku, setSelectedSku] = useState(skuOrder[0]);

  const skus = useMemo(() => skuOrder.map((id) => skuForecasts[id]), []);
  const current = skuForecasts[selectedSku];

  const totalSkus = skus.length;
  const avgMape = (skus.reduce((sum, s) => sum + s.mape, 0) / totalSkus).toFixed(1);
  const riskCount = skus.filter((s) => s.status === 'CRITICAL' || s.status === 'ORDER_NOW').length;
  const revenueForecast = skus.reduce((sum, s) => sum + s.nextMonthForecast * s.unitPrice, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-black pt-24 pb-16 relative z-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-white mb-4">Forecast Engine</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            K-beauty inventory forecasting with ML-powered demand prediction, confidence intervals, and promo impact analysis — 24 months of history, 6 months forecast ahead.
          </p>
        </div>

        {/* Portfolio KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="SKUs Tracked" value={totalSkus} />
          <StatCard label="Forecast Accuracy (MAPE)" value={`${avgMape}%`} sub="lower is better" />
          <StatCard label="Stockout Risk SKUs" value={riskCount} sub={riskCount > 0 ? 'needs action' : 'all clear'} tone={riskCount > 0 ? 'down' : 'up'} />
          <StatCard label="Next 30-Day Revenue Forecast" value={formatCurrency(revenueForecast)} sub="across all SKUs" tone="up" />
        </div>

        {/* SKU selector */}
        <div className="flex flex-wrap gap-2 mb-6">
          {skus.map((s) => (
            <button
              key={s.sku}
              onClick={() => setSelectedSku(s.sku)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                selectedSku === s.sku
                  ? 'bg-white text-purple-900 border-white'
                  : 'bg-white/10 text-gray-200 border-white/20 hover:bg-white/20'
              }`}
            >
              {s.name}
              <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_STYLES[s.status]}`}>
                {STATUS_LABEL[s.status]}
              </span>
            </button>
          ))}
        </div>

        {/* Main forecast chart */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">{current.name} — Demand History &amp; Forecast</h3>
            <span className="text-xs text-gray-300">Units / month</span>
          </div>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={current.rows} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#D1D5DB' }} interval={2} />
                <YAxis tick={{ fontSize: 10, fill: '#D1D5DB' }} tickFormatter={formatUnits} />
                <Tooltip
                  formatter={(value, name) => [value === null ? '—' : formatUnits(value), name]}
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: '#D1D5DB' }} />
                <Area type="monotone" dataKey="upper" name="Upper bound" stroke="none" fill="#A855F7" fillOpacity={0.12} />
                <Area type="monotone" dataKey="lower" name="Lower bound" stroke="none" fill="#111827" fillOpacity={1} />
                <Line type="monotone" dataKey="actual" name="Actual sales" stroke="#22D3EE" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#F472B6" strokeWidth={2} strokeDasharray="5 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-6">
            <StatCard label="Current Stock" value={formatUnits(current.currentStock)} />
            <StatCard
              label="Days of Cover"
              value={`${current.daysCover}d`}
              sub={STATUS_LABEL[current.status]}
              tone={current.status === 'CRITICAL' || current.status === 'ORDER_NOW' ? 'down' : 'up'}
            />
            <StatCard label="Recommended Reorder" value={formatUnits(current.reorderQty)} sub="units" />
            <StatCard label="YoY Demand Growth" value={`${current.yoyGrowth}%`} tone="up" />
          </div>

          <div className="mt-6 bg-purple-500/10 border border-purple-400/30 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-purple-300 uppercase tracking-wide mb-1.5">Insight</h4>
            <p className="text-sm text-gray-200 leading-relaxed">{buildInsight(current)}</p>
          </div>
        </div>

        {/* Reorder table + Promo calendar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-6">
            <h3 className="text-white font-semibold mb-4">Reorder Overview</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs text-gray-200">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-white/10">
                    <th className="py-2 pr-3 font-medium">SKU</th>
                    <th className="py-2 pr-3 font-medium text-right">Stock</th>
                    <th className="py-2 pr-3 font-medium text-right">Days Cover</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Reorder Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {skus.map((s) => (
                    <tr key={s.sku} className="border-b border-white/5 last:border-0">
                      <td className="py-2 pr-3 whitespace-nowrap">{s.name}</td>
                      <td className="py-2 pr-3 text-right">{formatUnits(s.currentStock)}</td>
                      <td className="py-2 pr-3 text-right">{s.daysCover}d</td>
                      <td className="py-2 pr-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_STYLES[s.status]}`}>
                          {STATUS_LABEL[s.status]}
                        </span>
                      </td>
                      <td className="py-2 text-right">{s.reorderQty > 0 ? formatUnits(s.reorderQty) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-6">
            <h3 className="text-white font-semibold mb-4">Promo Calendar Impact</h3>
            <div className="w-full h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={promoCalendar} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="campaign" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 9, fill: '#D1D5DB' }} height={60} />
                  <YAxis tick={{ fontSize: 10, fill: '#D1D5DB' }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    formatter={(value) => [`+${value}%`, 'Demand lift']}
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Bar dataKey="liftPct" name="Demand Lift %" fill="#F472B6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Average promo lift across the portfolio is {Math.round(promoCalendar.reduce((sum, p) => sum + p.liftPct, 0) / promoCalendar.length)}% — 11.11 and CNY campaigns consistently drive the strongest spikes.
            </p>
          </div>
        </div>

        {/* Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-2">Features</h3>
            <ul className="text-gray-300 space-y-2">
              <li>• Time series demand forecasting</li>
              <li>• Confidence interval bands</li>
              <li>• Reorder point recommendations</li>
              <li>• Promo impact analysis</li>
            </ul>
          </div>

          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-2">Tech Stack</h3>
            <ul className="text-gray-300 space-y-2">
              <li>• Facebook Prophet</li>
              <li>• Seasonality detection</li>
              <li>• Recharts visualization</li>
              <li>• React</li>
            </ul>
          </div>

          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-2">Use Cases</h3>
            <ul className="text-gray-300 space-y-2">
              <li>• Demand planning</li>
              <li>• Inventory replenishment</li>
              <li>• Promo calendar planning</li>
              <li>• Stockout prevention</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forecasting;
